import localize from '@/filters/localize';

// `localize()` has no {param} interpolation of its own (it's a flat key lookup), so this
// is the "tiny helper" that fills a template's {placeholders} from the event's params.
// A missing/null param is substituted with '' rather than left as a literal "{param}" —
// collapseSeparators() then cleans up the empty segment/separator that leaves behind.
function interpolate(template, params) {
  const filled = template.replace(/\{(\w+)\}/g, (match, key) => {
    const value = params[key];
    return (value !== undefined && value !== null) ? String(value) : '';
  });
  return collapseSeparators(filled);
}

// All templates use " · " to join their parts. A param substituted with '' leaves either
// a dangling separator at an end ("Request #123 · ") or a doubled one in the middle
// ("Request #123 ·  · €900") — split/filter/join removes the empty segment along with
// its separator instead of just leaving a gap.
function collapseSeparators(text, separator = ' · ') {
  return text
    .split(separator)
    .map(part => part.trim())
    .filter(Boolean)
    .join(separator)
    .trim();
}

function isMissing(text) {
  // localize() returns this exact sentinel when a key has no entry — see filters/localize.js
  return typeof text !== 'string' || text.startsWith('*NOT FOUND*');
}

// Renders the DISPLAY title/summary for a notification, per the viewer's current locale:
// - if the item carries a `data.event` key and both `notifications.events.<event>.title`
//   and `.summary` exist in the active locale file, render those with `data.params`
//   interpolated in;
// - otherwise fall back to the notification's own stored `title`/`summary` (the backend's
//   English copy), so nothing ever renders blank or a raw *NOT FOUND* string.
// Swedish templates in en.json/se.json are a best-effort draft and still want a native
// review before this ships (same caveat as the rest of the CX-21 locale keys).
export default function resolveNotificationText(item) {
  const event = item.data && item.data.event;
  if (event) {
    const params = item.data.params || {};
    const title = localize(`notifications.events.${event}.title`);
    const summary = localize(`notifications.events.${event}.summary`);
    if (!isMissing(title) && !isMissing(summary)) {
      return {title: interpolate(title, params), summary: interpolate(summary, params)};
    }
  }
  return {title: item.title, summary: item.summary};
}
